import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import type { CubeQueryEditorViewArgs, Query, QueryCubeArgs } from "@models/ISchema";
import query from "../../graphql/packageAnnualScheduleCell.query.graphql";

/**
 * Fetches the selected league's annual drawer detail and tracks the latest request.
 *
 * @param variables - Scenario, origin, and league ID to query.
 * @param scopeKey - Identity of the visible package and selected cell.
 * @param enabled - Whether the drawer has a complete selection and may request detail.
 * @returns Data for the current context, loading/error flags, and a refresh function.
 *
 * @remarks
 * Fetches from the network without reading or writing the Apollo cache. Responses from
 * earlier requests are ignored after a refresh, context change, or component unmount.
 * During a refresh, previous data is retained only when it belongs to the same context.
 * Disabling the hook skips requests while retaining its last data for the closing animation.
 * The GraphQL document is owned by this service; callers provide only request context.
 */
export const useAnnualQuery = (
  variables: QueryCubeArgs & Pick<CubeQueryEditorViewArgs, "resourceId">,
  scopeKey: string,
  enabled = true,
) => {
  const client = useApolloClient();
  const requestVersion = useRef(0);
  const { scenarioId, originId, resourceId } = variables;

  const [result, setResult] = useState<{
    key: string;
    data?: Pick<Query, "cube">;
    loading: boolean;
    error: boolean;
  }>({ key: scopeKey, loading: true, error: false });
  /**
   * Fetches the resource again and publishes its result only if the request is still current.
   *
   * @returns A promise that settles after processing the response. Failures, including a
   * missing `editorView`, are reported through `error` rather than propagated to the caller.
   */
  const refresh = useCallback(async () => {
    if (!enabled) return;

    const currentRequestVersion = ++requestVersion.current;

    setResult(previous => ({
      key: scopeKey,
      data: previous.key === scopeKey ? previous.data : undefined,
      loading: true,
      error: false,
    }));

    try {
      const { data } = await client.query<Pick<Query, "cube">>({
        query,
        variables: { scenarioId, originId, resourceId },
        fetchPolicy: "no-cache",
      });

      if (!data?.cube?.editorView) {
        throw new Error("Annual schedule is unavailable");
      }

      if (currentRequestVersion !== requestVersion.current) return;

      setResult({ key: scopeKey, data, loading: false, error: false });
    } catch {
      if (currentRequestVersion !== requestVersion.current) return;

      setResult({ key: scopeKey, loading: false, error: true });
    }
  }, [client, query, scenarioId, originId, resourceId, scopeKey, enabled]);

  useEffect(() => {
    if (enabled) void refresh();

    return () => {
      // Invalidates pending responses; the network request may continue.
      requestVersion.current += 1;
    };
  }, [enabled, refresh]);

  const isCurrentScope = result.key === scopeKey;

  return {
    data: isCurrentScope ? result.data : undefined,
    loading: !isCurrentScope || result.loading,
    error: isCurrentScope && result.error,
    refresh,
  };
};
